import { useEffect, useRef } from 'react'
import { rfStore } from '../lib/rfstore'
import { nivelParaCor, parseSUnits } from '../lib/sinalCores'

/**
 * S-meter segmentado, desenhado em canvas a 60 fps.
 *
 * Exemplo canônico da regra de performance: este componente renderiza UMA vez
 * no React e depois se atualiza sozinho por requestAnimationFrame lendo o store
 * externo. Nenhum setState no caminho quente.
 */
export function SMeter({ segments = 18 }: { segments?: number }) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0

    const draw = () => {
      const now = performance.now()
      const s = rfStore.snapshot()

      const dpr = window.devicePixelRatio || 1
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr
        canvas.height = h * dpr
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      // Em TX o instrumento vira wattímetro, como num rádio real.
      let lit = 0
      if (s.transmitting) {
        lit = segments - 2
      } else if (rfStore.isReceiving(now) && s.rx) {
        // S1 = -121 dBm, 6 dB por unidade. Mapeado sobre os segmentos.
        const su = parseSUnits(s.rx.s_meter)
        lit = Math.round((su / 12) * segments)
      }
      lit = Math.max(0, Math.min(segments, lit))

      const gap = 2
      const segW = (w - gap * (segments - 1)) / segments

      for (let i = 0; i < segments; i++) {
        const x = i * (segW + gap)
        // Gradiente contínuo frio→quente ao longo dos segmentos acesos, em
        // vez de um único degrau de cor em ~S9.
        ctx.fillStyle = i < lit ? nivelParaCor(i / (segments - 1)) : '#2b323a'
        ctx.fillRect(x, 0, segW, h)
      }

      raf = requestAnimationFrame(draw)
    }

    raf = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(raf)
  }, [segments])

  return <canvas ref={ref} style={{ width: '100%', height: 14, display: 'block' }} />
}
